<template>
  <v-card
    :loading="loading"
    class="pa-0"
  >
    <v-card-title>
      <!--
        What the pages in view have cost to generate. Tracks the tab's own filter rather than the whole
        table, so the number always describes what you are looking at. Hidden until something has been
        recorded, so tabs of pages generated before spend was attributed don't read as free.
      -->
      <span
        v-if="totals.calls"
        class="body-2 grey--text text--darken-1"
      >
        {{ totals.pages }} pages · {{ totals.calls }} AI calls · {{ totals.cost | currency }}
      </span>
      <v-spacer />
      <v-btn
        v-if="selectedRewritable.length"
        outlined
        color="mcsprimary"
        class="mr-2"
        @click="bulkDialog = true"
      >
        Rewrite {{ selectedRewritable.length }} selected
      </v-btn>
      <v-btn @click="showAddPage"> Add Page </v-btn>
      <v-btn
        outlined
        class="ml-2"
        @click="$emit('reload')"
      >
        Refresh
      </v-btn>
    </v-card-title>
    <v-card-text class="pa-0">
      <v-data-table
        v-model="selected"
        :items="pagesToShow"
        :headers="headers"
        item-key="id"
        :items-per-page="-1"
        show-select
        show-expand
        :expanded.sync="expanded"
        :item-selectable="isRewritable"
        @item-expanded="loadSpend"
      >
        <template #[`header.published`]="{}">
          <v-icon small> wifi </v-icon>
        </template>

        <template #[`item.published`]="{ item }">
          <v-icon
            v-if="item.id"
            small
            :color="item.published ? 'success' : 'error'"
          >
            wifi
          </v-icon>
        </template>
        <template #[`item.typeId`]="{ item }">
          {{ types.filter((x) => x.id == item.typeId).map((x) => x.name)[0] }}
        </template>
        <template #[`item.generationStatus`]="{ item }">
          <v-chip
            v-if="item.id"
            small
            :color="statusColor(item.generationStatus)"
            :outlined="item.generationStatus === 'Ready' || item.generationStatus === 'Published'"
            dark
          >
            {{ statusText(item) }}
          </v-chip>
        </template>
        <template #[`item.rootKeywords`]="{ item }">
          <v-chip
            v-for="(keyword, i) in (item.rootKeywords || []).slice(0, 3)"
            :key="keyword"
            x-small
            outlined
            class="mr-1 my-1"
            :color="i === 0 ? 'mcsprimary' : undefined"
          >
            {{ keyword }}
          </v-chip>
          <span
            v-if="(item.rootKeywords || []).length > 3"
            class="caption grey--text"
          >
            +{{ item.rootKeywords.length - 3 }}
          </span>
        </template>

        <!--
          Cost over call count. A page with nothing recorded shows an em dash rather than $0.00: it almost
          always means the page was generated before spend carried a page id, and writing "free" for that
          would be a lie about a page that cost as much as its neighbours.
        -->
        <template #[`item.aiCost`]="{ item }">
          <template v-if="item.aiCalls">
            <div>{{ item.aiCost | currencyAi }}</div>
            <div class="caption grey--text">{{ item.aiCalls }} calls</div>
          </template>
          <span
            v-else-if="item.id"
            class="grey--text text--lighten-1"
          >
            —
          </span>
        </template>

        <!--
          The per-step / per-section breakdown, fetched the first time a row is opened and then kept, so
          re-opening a row is free. Answers "why did this page cost that": rows come back most expensive
          first, so the reason is the top line.
        -->
        <template #expanded-item="{ headers, item }">
          <td
            :colspan="headers.length"
            class="pa-0"
          >
            <div
              v-if="!breakdowns[item.id]"
              class="pa-4 caption grey--text"
            >
              Loading AI spend…
            </div>
            <div
              v-else-if="!breakdowns[item.id].rows.length"
              class="pa-4 caption grey--text"
            >
              No AI spend recorded for this page. Pages generated before cost tracking shipped have no
              ledger entries tying their calls to the page.
            </div>
            <v-simple-table
              v-else
              dense
              class="ma-4"
            >
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Part</th>
                  <th class="text-end">Calls</th>
                  <th class="text-end">Tokens</th>
                  <th class="text-end">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, i) in breakdowns[item.id].rows"
                  :key="`${row.taskKey}_${row.refDetail}_${i}`"
                >
                  <td>{{ stepLabel(row.taskKey) }}</td>
                  <td>{{ row.refDetail || '—' }}</td>
                  <td class="text-end">{{ row.calls }}</td>
                  <td class="text-end">
                    {{ row.inputTokens + row.outputTokens ? (row.inputTokens + row.outputTokens) : '—' }}
                  </td>
                  <td class="text-end">{{ row.cost | currencyAi }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </td>
        </template>

        <!--
          Icon buttons on one line. Six full-size buttons wrapped the cell and made every row several
          hundred pixels tall; each of these is one click and names itself on hover. Destructive and
          consequential actions still go through their confirm dialogs.
        -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex align-center justify-end flex-nowrap">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="black"
                  v-bind="attrs"
                  v-on="on"
                  @click="edit(item)"
                >
                  <v-icon small> edit </v-icon>
                </v-btn>
              </template>
              <span>Edit JSON</span>
            </v-tooltip>

            <v-tooltip
              v-if="isRewritable(item)"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="mcsprimary"
                  :loading="saving"
                  v-bind="attrs"
                  v-on="on"
                  @click="rewrite(item)"
                >
                  <v-icon small> autorenew </v-icon>
                </v-btn>
              </template>
              <span>Rewrite from the current blueprint</span>
            </v-tooltip>

            <v-tooltip
              v-if="!item.published && item.generationStatus === 'Ready'"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="success"
                  :loading="saving"
                  v-bind="attrs"
                  v-on="on"
                  @click="publish(item)"
                >
                  <v-icon small> publish </v-icon>
                </v-btn>
              </template>
              <span>Publish</span>
            </v-tooltip>

            <!--
              One button, not two: "Preview" and "Needs Review" always pointed at the same url and only
              differed in colour. The colour IS the signal, so it stays and the duplicate goes.
            -->
            <v-tooltip
              v-if="item.id"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :color="item.needsReview ? 'error' : 'mcsprimary'"
                  :href="item.fullUrl"
                  target="_blank"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small> open_in_new </v-icon>
                </v-btn>
              </template>
              <span>{{ item.needsReview ? 'Preview — needs review' : 'Preview' }}</span>
            </v-tooltip>

            <v-tooltip
              v-if="!item.published"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  :loading="saving"
                  v-bind="attrs"
                  v-on="on"
                  @click="deletePage(item.id)"
                >
                  <v-icon small> delete </v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
    <Edit ref="editPage" />

    <v-dialog
      v-model="bulkDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title>Rewrite {{ selectedRewritable.length }} page(s)</v-card-title>
        <v-card-text>
          <p class="body-2">
            Each page is rebuilt from the current blueprint as a new unpublished draft on the same slug. The
            live page stays up until you publish its replacement.
          </p>
          <p class="body-2 error--text">
            Every rewritten page costs paid keyword research and AI writing, and that spend cannot be undone.
            Start with a handful, review the results and check the bill before selecting the rest.
          </p>
          <v-list dense>
            <v-list-item
              v-for="item in selectedRewritable.slice(0, 10)"
              :key="item.id"
              class="px-0"
            >
              <v-list-item-content class="py-1">
                <v-list-item-title class="body-2">{{ item.slug }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p
            v-if="selectedRewritable.length > 10"
            class="caption grey--text"
          >
            and {{ selectedRewritable.length - 10 }} more
          </p>
          <v-checkbox
            v-model="bulkAutoPublish"
            label="Publish automatically when generation completes"
            hint="Leave off to review every page before it replaces the live one."
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="bulkDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="mcsprimary"
            :loading="saving"
            @click="rewriteSelected"
          >
            Queue Rewrites
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Edit from '@/components/content/pages/Edit';
import { sortBy } from 'lodash';
import { mapGetters } from 'vuex';
export default {
  components: { Edit },
  props: { type: { type: String, default: '' }, loading: { type: Boolean, default: false } },
  data() {
    return {
      saving: false,
      selected: [],
      expanded: [],
      // pageId -> the getPageAiSpend payload. Cached so collapsing and re-opening a row doesn't re-fetch.
      breakdowns: {},
      bulkDialog: false,
      bulkAutoPublish: false
    };
  },

  computed: {
    // Only a LIVE page can be rewritten — an unpublished one is already a draft, and a blog post has no
    // lookup row to rebuild its subject from. The server re-checks this; filtering here keeps the button
    // count honest rather than promising work that will be skipped.
    selectedRewritable() {
      return this.selected.filter((x) => this.isRewritable(x));
    },
    headers() {
      const list = [
        { value: 'published' },
        { text: 'Type', value: 'typeId' },
        { text: 'Slug', value: 'slug' },
        { text: 'Status', value: 'generationStatus' },
        { text: 'Targeting', value: 'rootKeywords', sortable: false },
        { text: 'AI cost', value: 'aiCost', align: 'end', width: 110 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 160 }
      ];
      return list.filter((x) => !x.hide);
    },
    // Rolled up from the rows actually on screen, so the figure describes the tab you are on rather
    // than every content page we have.
    totals() {
      const list = this.pagesToShow;
      return {
        pages: list.length,
        calls: list.reduce((sum, x) => sum + (x.aiCalls || 0), 0),
        cost: list.reduce((sum, x) => sum + (x.aiCost || 0), 0)
      };
    },
    pagesToShow() {
      let list = this.pages;

      if (this.type === 'needs_attention') {
        list = list.filter((x) => !x.published || x.needsImages || x.needsReview);
      } else {
        list = list.filter((x) => x.typeId.toString() === this.type);
      }

      return sortBy(list, 'url');
    },
    ...mapGetters({
      pages: 'content/pages',
      master_accounts: 'lookup/master_accounts',
      types: 'lookup/content_page_types'
    })
  },

  watch: {
    // Switching tabs swaps the whole list out. Selections made on the previous tab are still held by the
    // table but no longer visible, so without this you could queue rewrites for pages you can't see.
    type() {
      this.selected = [];
      // Same reason the selection is dropped: an expanded row from the previous tab is no longer in the
      // table, and leaving it open makes the first row of the new tab appear expanded.
      this.expanded = [];
    }
  },

  async mounted() {},

  methods: {
    // Fetches a page's cost breakdown the first time its row is opened. Vuetify fires this on collapse
    // too, hence the `value` guard, and the cache means a row you keep opening only ever costs one call.
    loadSpend({ item, value }) {
      if (!value || !item || !item.id || this.breakdowns[item.id]) {
        return;
      }

      this.$store
        .dispatch('content/getPageAiSpend', item.id)
        .then((result) => {
          this.$set(this.breakdowns, item.id, result || { rows: [] });
        })
        .catch((error) => {
          // Leave the row on "Loading…" rather than caching a failure, so closing and re-opening retries.
          this.logError(error);
        });
    },
    // The generation step a ledger row belongs to. Falls back to the raw task key so a step added later
    // still shows up in the breakdown instead of rendering blank.
    stepLabel(taskKey) {
      const labels = {
        'content.competitor': 'Competitors',
        'content.section': 'Sections',
        'content.seo': 'SEO',
        'image.contentpage': 'Images'
      };
      return labels[taskKey] || taskKey;
    },
    // "Scheduled" on its own says nothing useful — when it goes live is the whole point — so the chip carries
    // the date. Sliced out of the plain server-local string rather than parsed, so the browser's timezone
    // can't shift a 9am schedule an hour either way.
    statusText(item) {
      if (item.generationStatus !== 'Scheduled') {
        return item.generationStatus;
      }
      const [date, time] = (item.publishedDate || '').split('T');
      return `Scheduled ${date} ${(time || '').substring(0, 5)}`.trim();
    },
    statusColor(status) {
      switch (status) {
        case 'Researching competitors':
          return 'purple';
        case 'Writing content':
          return 'blue';
        case 'Writing SEO':
          return 'indigo';
        case 'Generating images':
          return 'orange';
        // Not a stage — a stop. The poller has exhausted its attempts on this page's prompt and will not
        // try again, so this one only moves when somebody touches it.
        case 'Image generation failed':
          return 'error';
        case 'Scheduled':
          return 'teal';
        case 'Published':
          return 'success';
        default:
          return 'grey';
      }
    },
    showAddPage() {
      this.$bus.$emit(this.$enums.EVENT_BUS_EVENTS.PAGE_ADD_SHOW, {
        type: 'page'
      });
    },
    edit(item) {
      this.$refs.editPage.show(item.id);
    },
    async rewrite(item) {
      if (
        !(await this.$root.$confirm(
          'Rewrite Page',
          `Rebuild ${item.slug} from the current blueprint? A new draft is created on the same slug and this page stays live until you publish it. Keyword research and AI writing are billable.`,
          { color: 'mcsprimary' }
        ))
      ) {
        return;
      }

      this.saving = true;
      this.$store
        .dispatch('content/rewritePage', item.id)
        .then((result) => {
          if (result && result.alreadyInFlight) {
            this.$root.$snackbarSuccess('That page already has a draft in progress');
          } else if (result && result.created) {
            this.$root.$snackbarSuccess('Rewrite queued, the draft appears below');
          } else {
            // Rewrite returns the page's own id when it declined — not published, a blog post, or the
            // subject's url segment has moved since the page was created. Sentry has the reason.
            this.$root.$snackbarSuccess('Nothing to rewrite for that page');
          }
          this.$emit('reload');
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    // ContentPageType.Blog === 6.
    isRewritable(item) {
      return !!item && !!item.id && item.published && item.typeId !== 6;
    },
    async rewriteSelected() {
      this.saving = true;
      this.$store
        .dispatch('content/rewritePages', {
          pageIds: this.selectedRewritable.map((x) => x.id),
          autoPublish: this.bulkAutoPublish
        })
        .then((result) => {
          this.bulkDialog = false;
          this.selected = [];
          var queued = (result || {}).queued || 0;
          var skipped = (result || {}).skipped || 0;
          this.$root.$snackbarSuccess(
            skipped
              ? `Queued ${queued} for rewrite, skipped ${skipped}`
              : `Queued ${queued} for rewrite`
          );
          this.$emit('reload');
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async publish(item) {
      if (
        !(await this.$root.$confirm(
          'Publish Page',
          `Take ${item.slug} live? Any page currently published on that url is retired.`,
          { color: 'success' }
        ))
      ) {
        return;
      }

      this.saving = true;
      this.$store
        .dispatch('content/publishPage', item.id)
        .then(() => {
          this.$root.$snackbarSuccess('Page published');
          this.$emit('reload');
        })
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async deletePage(pageId) {
      if (
        await this.$root.$confirm('Delete Page', 'Are you sure you want to delete this page?', {
          color: 'error'
        })
      ) {
        this.deleting = true;
        this.$store
          .dispatch('content/deletePage', pageId)
          .then(() => {
            this.$root.$snackbarSuccess('Page deleted');
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    }
  }
};
</script>
